import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Sparkles,
  ChevronDown,
  Brain,
  HeartPulse,
  Target,
  Users,
} from "lucide-react";
import CosmicBackground from "./CosmicBackground";
import GlowingFootball from "./decor/GlowingFootball";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_electric-game-2/artifacts/na4ceq8a_Future%20Football%20ny%20-%20den%20rigtige%20og%20udvalgte.png";

const PILLAR_ICONS = [Brain, HeartPulse, Target, Users];
const PILLAR_COLORS = ["#FF3DCB", "#FF2D5C", "#9B4DFF", "#6FF6FF"];

const Hero = () => {
  const { t } = useTranslation();
  const marquee = t("hero.marquee", { returnObjects: true });
  const pillars = t("hero.pillars", { returnObjects: true });

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      data-testid="hero-section"
      className="relative isolate w-full overflow-hidden pb-24 md:pb-32"
      style={{
        // iPhone safe-area + responsive top padding (mobile 80px → desktop 128px)
        paddingTop: "calc(max(env(safe-area-inset-top), 0px) + clamp(5rem, 8vw, 8rem))",
      }}
    >
      {/* Layered cosmic universe behind everything */}
      <CosmicBackground variant="rich" />

      {/* Floating glowing footballs — fewer, calmer, more cinematic.
          Each one shares the EXACT geometry of the BrandLogo football.
          Sizes use clamp() so they scale gracefully on mobile. */}
      <GlowingFootball
        size={150}
        color="#9B4DFF"
        className="top-[6%] right-[3%] opacity-55"
        speed={95}
        delay={-2}
        intensity={0.85}
      />
      <GlowingFootball
        size={130}
        color="#FF2D5C"
        className="bottom-[-2%] right-[42%] opacity-45 hidden md:block"
        speed={120}
        delay={-14}
        intensity={0.7}
      />
      <GlowingFootball
        size={70}
        color="#3DBFFF"
        className="bottom-[18%] left-[18%] opacity-50 hidden sm:block"
        speed={140}
        delay={-22}
        intensity={0.75}
      />
      <GlowingFootball
        size={56}
        color="#FF3DCB"
        className="top-[22%] left-[38%] opacity-40 hidden lg:block"
        speed={155}
        delay={-8}
        intensity={0.7}
      />

      <div className="relative mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-8">
          {/* Left — copy */}
          <div className="lg:col-span-6 xl:col-span-6 flex flex-col justify-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="eyebrow"
            >
              <Sparkles className="h-3 w-3" />
              {t("hero.eyebrow")}
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.1 }}
              className="font-display mt-6 text-[clamp(1.3rem,2.9vw,2.7rem)] font-semibold uppercase leading-[1.06] tracking-[0]"
            >
              <span
                className="block text-white"
                style={{
                  textShadow:
                    "0 0 14px rgba(255,255,255,0.28), 0 0 4px rgba(255,255,255,0.5)",
                }}
              >
                {t("hero.title_pre")}
              </span>
              <span
                className="block"
                style={{
                  color: "#6FF6FF",
                  textShadow:
                    "0 0 14px rgba(111,246,255,0.45), 0 0 32px rgba(61,191,255,0.25)",
                }}
              >
                {t("hero.title_main")}
              </span>
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.25 }}
              className="mt-7 max-w-xl"
            >
              <p className="font-display text-lg md:text-xl font-medium text-white/85 leading-snug">
                {t("hero.subtitle_lead")}
              </p>
              <p className="mt-5 text-sm md:text-[15px] leading-relaxed text-white/65 max-w-lg">
                {t("hero.body")}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.4 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <button
                data-testid="hero-cta-primary"
                onClick={() => scrollTo("kontakt")}
                className="group inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm md:text-[15px] uppercase tracking-[0.12em] btn-neon-primary"
              >
                {t("hero.cta_primary")}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
              <button
                data-testid="hero-cta-secondary"
                onClick={() => scrollTo("metoden")}
                className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm md:text-[15px] uppercase tracking-[0.12em] btn-neon-ghost"
              >
                {t("hero.cta_secondary")}
              </button>
            </motion.div>
          </div>

          {/* Right — bigger, less-constrained hero image */}
          <div className="lg:col-span-6 xl:col-span-6 hidden lg:flex items-center justify-end relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.4, delay: 0.2 }}
              className="relative w-full max-w-[680px] aspect-square"
              style={{
                marginRight: "-2rem",
              }}
            >
              {/* Outer ambient halo bleeds way past the image edges into the universe */}
              <div
                aria-hidden
                className="absolute -inset-32 rounded-full opacity-80 blur-3xl pulse-glow"
                style={{
                  background:
                    "radial-gradient(circle at 50% 50%, rgba(255,45,92,0.42) 0%, rgba(255,61,203,0.32) 28%, rgba(155,77,255,0.28) 50%, rgba(61,191,255,0.22) 72%, transparent 88%)",
                }}
              />
              {/* Image with soft radial fade-out (no harsh circle) */}
              <div
                className="relative h-full w-full"
                style={{
                  WebkitMaskImage:
                    "radial-gradient(circle at 50% 50%, #000 52%, rgba(0,0,0,0.6) 70%, transparent 92%)",
                  maskImage:
                    "radial-gradient(circle at 50% 50%, #000 52%, rgba(0,0,0,0.6) 70%, transparent 92%)",
                }}
              >
                <img
                  src={HERO_IMG}
                  alt="EleKtro Universal Future Football™"
                  className="absolute inset-0 h-full w-full object-cover"
                  loading="eager"
                />
              </div>
            </motion.div>
          </div>
        </div>

        {/* 4-pillar feature row — collective flow identity moment */}
        {Array.isArray(pillars) && pillars.length === 4 && (
          <motion.div
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-20 grid gap-7 md:grid-cols-2 lg:grid-cols-4"
          >
            {pillars.map((p, i) => {
              const Icon = PILLAR_ICONS[i];
              const color = PILLAR_COLORS[i];
              return (
                <div
                  key={i}
                  data-testid={`hero-pillar-${i}`}
                  className="relative flex items-start gap-4"
                >
                  <span
                    className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-white/10"
                    style={{
                      background: `linear-gradient(135deg, ${color}28, transparent)`,
                      boxShadow: `0 0 22px -6px ${color}cc, inset 0 0 12px -4px ${color}66`,
                    }}
                  >
                    <Icon className="h-6 w-6" style={{ color }} />
                  </span>
                  <div className="flex flex-col">
                    <span
                      className="font-display text-[12.5px] uppercase font-bold tracking-[0.16em]"
                      style={{
                        color,
                        textShadow: `0 0 10px ${color}99`,
                      }}
                    >
                      {p.title}
                    </span>
                    <p className="mt-2 text-[13px] leading-relaxed text-white/70">
                      {p.body}
                    </p>
                  </div>
                </div>
              );
            })}
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-14 flex items-center gap-3 text-[10px] font-mono uppercase tracking-[0.3em] text-white/45"
        >
          <ChevronDown className="h-3 w-3 animate-bounce" />
          {t("hero.scroll_label")}
        </motion.div>
      </div>

      {/* Marquee */}
      {Array.isArray(marquee) && (
        <div className="absolute bottom-0 left-0 right-0 border-y border-white/5 bg-black/40 backdrop-blur-sm">
          <div className="marquee-track py-3">
            {[...marquee, ...marquee, ...marquee].map((item, i) => (
              <span
                key={i}
                className="mx-8 inline-flex items-center gap-3 font-display text-sm md:text-base text-white/70"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-[#FF3DCB] shadow-[0_0_10px_#FF3DCB]" />
                {item}
              </span>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default Hero;
